import React, { useMemo, useState } from "react";
import { HEADERS, COL } from "../api/sheets";
import PictureModal from "./PictureModal";
import { useT } from "../i18n";
import { Row } from "../types/sheets";

interface EditableTableProps {
  rows: Row[];
  onCellSave: (rowIndex: number, colIndex: number, value: string) => void;
  onCopyReportLink: (id: string) => void;
  onAddPhotos?: (id: string) => void;
  onDeleteRow?: (rowIndex: number, id: string) => void;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  onToggleSelectAll?: (ids: string[]) => void;
}

export default function EditableTable({
  rows,
  onCellSave,
  onCopyReportLink,
  onAddPhotos,
  onDeleteRow,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
}: EditableTableProps) {
  const t = useT();
  const [filters, setFilters] = useState({ campus: "", building: "", owner: "" });
  const [editing, setEditing] = useState<{ rowIndex: number; colIndex: number } | null>(null);
  const [draft, setDraft] = useState("");
  const [pictureIds, setPictureIds] = useState<string[] | null>(null);

  const filtered = useMemo(() => {
    return rows
      .map((r, i) => ({ r, i }))
      .filter(({ r }) => {
        if (filters.campus && !(String(r[COL["キャンパス"]] || "")).includes(filters.campus)) return false;
        if (filters.building && !(String(r[COL["建物"]] || "")).includes(filters.building)) return false;
        if (filters.owner && !(String(r[COL["担当者"]] || "")).includes(filters.owner)) return false;
        return true;
      });
  }, [rows, filters]);

  const selectableIds = useMemo(
    () => filtered.map(({ r }) => String(r[COL["ID"]] || "")).filter(Boolean),
    [filtered]
  );
  const allSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selectedIds?.has(id));

  function startEdit(rowIndex: number, colIndex: number, value: unknown) {
    setEditing({ rowIndex, colIndex });
    setDraft(String(value || ""));
  }

  async function commitEdit() {
    if (!editing) return;
    const { rowIndex, colIndex } = editing;
    const current = String(rows[rowIndex][colIndex] || "");
    if (draft !== current) {
      await onCellSave(rowIndex, colIndex, draft);
    }
    setEditing(null);
  }

  const missingGps = (r: Row) => !(r[COL["緯度"]] && r[COL["経度"]]);

  interface FilterInputProps {
    icon: string;
    placeholder: string;
    value: string;
    onChange: React.ChangeEventHandler<HTMLInputElement>;
  }

  function FilterInput({ icon, placeholder, value, onChange }: FilterInputProps) {
    return (
      <div className="relative">
        <i className={`fa-solid ${icon} absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs`} aria-hidden="true" />
        <input
          placeholder={placeholder}
          className="border rounded-md pl-7 pr-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
          value={value}
          onChange={onChange}
        />
      </div>
    );
  }

  const selectable = !!onToggleSelect;

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex flex-wrap gap-2 p-2 border-b bg-gray-50">
        <FilterInput
          icon="fa-building-columns"
          placeholder={t("table.filterCampus")}
          value={filters.campus}
          onChange={(e) => setFilters((f) => ({ ...f, campus: e.target.value }))}
        />
        <FilterInput
          icon="fa-building"
          placeholder={t("table.filterBuilding")}
          value={filters.building}
          onChange={(e) => setFilters((f) => ({ ...f, building: e.target.value }))}
        />
        <FilterInput
          icon="fa-user"
          placeholder={t("table.filterOwner")}
          value={filters.owner}
          onChange={(e) => setFilters((f) => ({ ...f, owner: e.target.value }))}
        />
        <span className="text-sm text-gray-500 ml-auto self-center">
          {filtered.length} / {rows.length} {t("common.rows")}
        </span>
      </div>

      <div className="flex-1 overflow-auto">
        <table className="text-sm border-collapse w-full">
          <thead className="bg-gray-100 sticky top-0">
            <tr>
              {selectable && (
                <th className="border px-2 py-1 font-semibold text-gray-700">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={() => onToggleSelectAll?.(selectableIds)}
                    disabled={selectableIds.length === 0}
                    title={t("table.selectAll")}
                    aria-label={t("table.selectAll")}
                  />
                </th>
              )}
              {HEADERS.map((h) => (
                <th key={h} className="border px-2 py-1 text-left whitespace-nowrap font-semibold text-gray-700">
                  {t(`headers.${h}`)}
                </th>
              ))}
              <th className="border px-2 py-1 font-semibold text-gray-700">{t("table.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(({ r, i }) => {
              const id = String(r[COL["ID"]] || "");
              return (
                <tr key={i} className={missingGps(r) ? "bg-yellow-50" : "hover:bg-gray-50"}>
                  {selectable && (
                    <td className="border px-2 py-0.5 text-center align-top">
                      <input
                        type="checkbox"
                        checked={!!id && !!selectedIds?.has(id)}
                        onChange={() => onToggleSelect?.(id)}
                        disabled={!id}
                        title={id ? t("table.selectRow") : t("table.selectRowNoId")}
                        aria-label={t("table.selectRow")}
                      />
                    </td>
                  )}
                  {HEADERS.map((h, c) => {
                    const readOnly = c === COL["表記名①"];
                    return (
                      <td
                        key={h}
                        className={`border px-1 py-0.5 align-top ${
                          readOnly ? "bg-gray-50 text-gray-500 cursor-not-allowed" : ""
                        }`}
                        title={readOnly ? t("table.derivedDisplayName") : undefined}
                        onClick={readOnly ? undefined : () => startEdit(i, c, r[c])}
                      >
                        {!readOnly && editing && editing.rowIndex === i && editing.colIndex === c ? (
                          <input
                            autoFocus
                            className="w-full border rounded px-1"
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onBlur={commitEdit}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") commitEdit();
                              else if (e.key === "Escape") setEditing(null);
                            }}
                          />
                        ) : (
                          <span className="whitespace-pre-wrap break-words">{String(r[c] ?? "")}</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="border px-1 py-0.5 whitespace-nowrap">
                    <button
                      className="inline-flex items-center gap-1 text-xs text-pink-600 hover:text-pink-800 hover:underline"
                      onClick={() => onCopyReportLink(String(r[COL["ID"]] || ""))}
                      title={t("table.reportUrl")}
                    >
                      <i className="fa-solid fa-link" aria-hidden="true" />
                      <span>{t("table.reportUrl")}</span>
                    </button>
                    {onAddPhotos && (
                      <button
                        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline ml-2"
                        onClick={() => onAddPhotos(String(r[COL["ID"]] || ""))}
                        title={t("table.photos")}
                      >
                        <i className="fa-solid fa-camera" aria-hidden="true" />
                        <span>{t("table.photos")}</span>
                      </button>
                    )}
                    {r[COL["写真ファイルID"]] && (
                      <button
                        className="inline-flex items-center gap-1 text-xs text-green-600 hover:text-green-800 hover:underline ml-2"
                        onClick={() =>
                          setPictureIds(
                            String(r[COL["写真ファイルID"]])
                              .split(",")
                              .map((s) => s.trim())
                              .filter(Boolean)
                          )
                        }
                        title={t("table.viewPictures")}
                      >
                        <i className="fa-solid fa-image" aria-hidden="true" />
                        <span>{t("table.pictures")}</span>
                      </button>
                    )}
                    {!missingGps(r) && (
                      <a
                        className="inline-flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800 hover:underline ml-2"
                        href={`https://www.google.com/maps/search/?api=1&query=${r[COL["緯度"]]},${r[COL["経度"]]}`}
                        target="_blank"
                        rel="noreferrer"
                        title={t("table.openInMaps")}
                      >
                        <i className="fa-solid fa-map-location-dot" aria-hidden="true" />
                        <span>{t("table.openInMaps")}</span>
                      </a>
                    )}
                    {onDeleteRow && (
                      <button
                        className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800 hover:underline ml-2"
                        onClick={() => onDeleteRow(i, String(r[COL["ID"]] || ""))}
                        title={t("table.delete")}
                      >
                        <i className="fa-solid fa-trash" aria-hidden="true" />
                        <span>{t("table.delete")}</span>
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pictureIds && (
        <PictureModal fileIds={pictureIds} onClose={() => setPictureIds(null)} />
      )}
    </div>
  );
}
